import type { Department } from './department'

export type CaseStatus = 'draft' | 'published' | 'scheduled'

export type CasePhase = 'pre_op' | 'post_op'

export type CaseState = 'under_review' | 'review_closed' | 'archived'

export type Gender = 'male' | 'female'

export type NamedRef = {
  id: string
  name: string
}

export type FilterOption = {
  value: string
  label: string
}

export type CaseUserRef = {
  id: string
  name: string
  email?: string
}

export type ReviewerType = 'doctor' | 'assistant'

export type CaseReviewerPickerOption = {
  id: string
  name: string
  email: string
  reviewer_type: ReviewerType
  registration_number?: string | null
  department_name?: string | null
  hospitals?: string[]
  owned_cases_count?: number
  public_cases_count?: number
}

export type CaseReviewerPickerResponse = {
  doctors: CaseReviewerPickerOption[]
  assistants: CaseReviewerPickerOption[]
}

export type CommentTag = 'pre_op' | 'post_op' | 'national'

export type CommentChannel = 'review' | 'national'

export type CaseFileType = 'pre_op' | 'post_op' | 'outcome'

export type CaseReviewer = {
  id: string
  case_id: string
  user_id: string
  reviewer_type: ReviewerType | null
  assigned_by: string | null
  original_user_id: string | null
  user?: CaseUserRef | null
  created_at: string | null
  updated_at: string | null
}

export type CaseComment = {
  id: string
  case_id: string
  user_id: string | null
  original_user_id: string | null
  is_anonymous?: boolean
  is_own?: boolean
  body: string
  tag: CommentTag | null
  channel?: CommentChannel
  parent_id: string | null
  user?: CaseUserRef | null
  created_at: string | null
  updated_at: string | null
}

export type CaseFile = {
  id: string
  case_id: string
  disk: string
  folder: string
  filename: string
  original_name: string
  mime: string
  size: number
  type: CaseFileType | null
  uploaded_by: string | null
  uploaded_by_user?: CaseUserRef | null
  preview_url?: string | null
  preview_expires_at?: string | null
  created_at: string | null
  updated_at: string | null
}

export type CaseActivity = {
  id: string
  subject_type: string
  subject_id: string
  actor_id: string | null
  type: string | null
  description: string | null
  properties: Record<string, unknown> | null
  actor?: CaseUserRef | null
  created_at: string | null
}

export type MedicalCase = {
  id: string
  reference: string
  belongs_to_doctor_id: string
  uploaded_by_user_id: string
  department_id: string | null
  procedure_id: string | null
  age: number | null
  gender: Gender | null
  procedure_date: string | null
  status: CaseStatus | null
  phase: CasePhase
  is_under_review: boolean
  is_review_closed: boolean
  is_archived: boolean
  is_global: boolean
  /** Owner opt-in: doctors may post National Hub comments on this public case. */
  allow_national_comments?: boolean
  /** Doctor opt-in: linked assistants can see this case in Cases for Review. */
  open_for_assistant_review?: boolean
  is_bookmarked?: boolean
  /** Current user is assigned to this case while acting as a case reviewer. */
  is_assigned_reviewer?: boolean
  /** Assignment grants contextual read/comment access only (not owner/uploader authority). */
  is_reviewer_only?: boolean
  operative_time: number | null
  description?: string | null
  past_medical_history: string | null
  note: string | null
  outcome: string | null
  verification_status: string | null
  belongs_to_doctor?: CaseUserRef | null
  uploaded_by?: CaseUserRef | null
  department?: NamedRef | null
  procedure?: NamedRef | null
  comorbidities?: NamedRef[]
  complications?: NamedRef[]
  reviewers?: CaseReviewer[]
  created_at: string | null
  updated_at: string | null
  deleted_at: string | null
}

export type Case = MedicalCase

export type CaseFormPayload = {
  reference?: string
  belongs_to_doctor_id?: string
  department_id?: string
  procedure_id?: string
  age?: number
  gender?: Gender
  procedure_date?: string | null
  phase?: CasePhase
  operative_time?: number | null
  description?: string | null
  is_global?: boolean
  allow_national_comments?: boolean
  open_for_assistant_review?: boolean
  past_medical_history?: string | null
  note?: string | null
  outcome?: string | null
  comorbidity_ids?: string[]
  complication_ids?: string[]
}

export type CaseFiltersResponse = {
  statuses: FilterOption[]
  phases?: FilterOption[]
  states: FilterOption[]
  genders: FilterOption[]
  /** Nested tree (same shape as doctor signup). */
  departments: Department[]
  procedures: NamedRef[]
  comorbidities: NamedRef[]
  complications: NamedRef[]
  /** Doctor-only: own linked assistants for list filter. */
  assistants?: NamedRef[]
}

export type CaseListParams = {
  phase?: CasePhaseFilter
  status?: CaseStatus
  state?: CaseState
  search?: string
  /** Doctor-only: filter cases uploaded by a linked assistant. */
  uploaded_by?: string
  sort?: 'created_at' | 'updated_at' | 'reference' | 'procedure_date' | 'status'
  direction?: 'asc' | 'desc'
  with_trashed?: boolean
  /** When true, list only soft-deleted cases (Show Deleted filter). */
  only_trashed?: boolean
  bookmarked?: boolean
  page?: number
  per_page?: number
}

export type BookmarkResponse = {
  bookmarked: boolean
}

export type CaseFileDownloadResponse = {
  url: string
  expires_at: string
}

export const CASE_PHASE_LABELS = {
  pre_op: 'Pre-Op Cases',
  post_op: 'Post-Op Cases',
} as const

export type CasePhaseFilter = CasePhase

export const CASE_PHASE_FILTERS: CasePhaseFilter[] = ['pre_op', 'post_op']

export const CASE_PHASE_FIELD_LABELS: Record<CasePhase, string> = {
  pre_op: 'Pre-op',
  post_op: 'Post-op',
}

export const CASE_FILE_TYPE_LABELS: Record<CaseFileType, string> = {
  pre_op: 'Pre-op',
  post_op: 'Post-op',
  outcome: 'Outcome',
}

export const CASE_FILE_TYPES: CaseFileType[] = ['pre_op', 'post_op']

export const CASE_OUTCOME_FILE_TYPES: CaseFileType[] = ['outcome']

export const CASE_STATUS_LABELS: Record<CaseStatus, string> = {
  draft: 'Draft',
  published: 'Published',
  scheduled: 'Scheduled',
}

export const CASE_STATUSES: CaseStatus[] = ['draft', 'published', 'scheduled']

export const CASE_STATE_LABELS: Record<CaseState, string> = {
  under_review: 'Under Review',
  review_closed: 'Review Closed',
  archived: 'Archived',
}

export const CASE_STATES: CaseState[] = ['under_review', 'review_closed', 'archived']

/** @deprecated Use NamedRef */
export type CaseFilterOption = NamedRef
